import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useQuery, useMutation, useApolloClient } from '@apollo/react-hooks'
import useLocalStorage from '../../../hooks/useLocalStorage'
import useUrlParam from '../../../hooks/useUrlParam'
import {
  Card,
  CardBody,
  Button,
  Input,
  InputGroup,
  InputGroupAddon,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@/components/ui-compat'
import { useTranslation } from 'react-i18next'
import { Search, FolderPlus, Upload, LayoutGrid, List, X, Trash2, CloudUpload, Loader2, FolderInput, Send, Download } from 'lucide-react'
import { Button as ShadcnButton } from '@/components/ui/button'
import { Input as ShadcnInput } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { toastr } from 'react-redux-toastr'
import * as Query from '../../../api/gql/query'
import * as Mutation from '../../../api/gql/mutation'
import { useModuleActions } from '../../../hooks/useMyPermissions'

import FileManagerGrid from './FileManagerGrid'
import DocumentList from './DocumentList'
import Breadcrumbs from './Breadcrumbs'
import FolderModal from './FolderModal'
import DocUploadModal from './DocUploadModal'
import DocumentDetailModal from './DocumentDetailModal'
import { FilePreviewModal } from '@/components/file-preview'
import { buildPreviewIndex } from '../utils/document-preview'
import ShareLinkModal from './ShareLinkModal'
import FolderShareDialog from './FolderShareDialog'
import DocumentBrowserSkeleton from './DocumentBrowserSkeleton'
import DeleteFolderModal from './DeleteFolderModal'
import MoveToFolderModal from './MoveToFolderModal'
import QuickTransmittalModal, { SelectedDocument } from './QuickTransmittalModal'
import { Alert } from '@/components/ui-compat'
import {
  FolderUploadData,
  readDroppedItems,
  hasDirectoryEntries,
} from '../utils/folder-upload'
import { computeFolderContentStats, findFolderInTree } from '../utils/folder-stats'
import { levelAtLeast } from '../utils/permission-levels'

interface Folder {
  id: string
  name: string
  description: string | null
  parentId: string | null
  documentCount: number
  children?: Folder[]
}

type ViewMode = 'grid' | 'list'

const DocumentBrowser: React.FC = () => {
  const { t } = useTranslation()
  const [currentFolderId, setCurrentFolderId] = useUrlParam('folderId')
  const [documentIdParam, setDocumentIdParam] = useUrlParam('documentId')
  const [searchQuery, setSearchQuery] = useState('')
  const [folderModalOpen, setFolderModalOpen] = useState(false)
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null)
  const [uploadModalOpen, setUploadModalOpen] = useState(false)
  const [documentDetailId, setDocumentDetailId] = useState<string | null>(null)
  const [viewMode, setViewMode] = useLocalStorage('docControl_viewMode', 'grid') as [ViewMode, (value: ViewMode) => void]

  // Selection state
  const [selectedDocIds, setSelectedDocIds] = useState<string[]>([])
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false)
  const [isBulkDeleting, setIsBulkDeleting] = useState(false)

  // "Send as Transmittal" state
  const [transmittalModalOpen, setTransmittalModalOpen] = useState(false)
  const [transmittalInitialDocs, setTransmittalInitialDocs] = useState<SelectedDocument[]>([])
  const [isPreparingTransmittal, setIsPreparingTransmittal] = useState(false)
  const [isPreparingDownload, setIsPreparingDownload] = useState(false)

  // Move state
  const [moveModalOpen, setMoveModalOpen] = useState(false)
  const [moveTargets, setMoveTargets] = useState<{ folderIds: string[]; documentIds: string[] }>({ folderIds: [], documentIds: [] })
  const [isMoving, setIsMoving] = useState(false)
  const [moveIsBulk, setMoveIsBulk] = useState(false)

  // Delete folder modal state
  const [deleteFolderTarget, setDeleteFolderTarget] = useState<{ id: string; name: string } | null>(null)

  // Share link modal state
  const [shareLinkModalOpen, setShareLinkModalOpen] = useState(false)
  const [shareTarget, setShareTarget] = useState<{ type: 'folder' | 'document'; id: string; name: string } | null>(null)

  // Folder access (share) dialog state
  const [accessFolderId, setAccessFolderId] = useState<string | null>(null)

  // Folder zip download in-flight (brief affordance on the trigger)
  const [downloadingFolderId, setDownloadingFolderId] = useState<string | null>(null)

  // Browser drag-and-drop state
  const [isBrowserDragging, setIsBrowserDragging] = useState(false)
  const [pendingFolderUpload, setPendingFolderUpload] = useState<FolderUploadData | null>(null)
  const [pendingFiles, setPendingFiles] = useState<File[] | null>(null)
  const dragCounter = useRef(0)

  const client = useApolloClient()

  // Get current project from ME query
  const { data: meData } = useQuery(Query.ME)
  const projectId = meData?.me?.currentProject?.id

  const { canCreate, canEdit, canDelete, canApprove, canAdmin } = useModuleActions('DOC_CONTROL', projectId)

  // Fetch folder tree (project-level)
  const { data: folderData, loading: foldersLoading, refetch: refetchFolders } = useQuery(Query.DOC_FOLDER_TREE, {
    variables: { projectId },
    skip: !projectId,
  })

  // allFolders (tree) carries effectivePermission per node — flatten for lookup
  const folderLevelById = useMemo(() => {
    const map = new Map<string, string | null>()
    const visit = (list: any[]) => {
      for (const f of list ?? []) {
        map.set(f.id, f.effectivePermission?.level ?? null)
        visit(f.children)
      }
    }
    visit(folderData?.docFolderTree ?? [])
    return map
  }, [folderData])

  const folderAtLeast = (folderId: string | null | undefined, min: string) =>
    !!folderId && levelAtLeast(folderLevelById.get(folderId), min)

  // Fetch documents for current folder
  const { data: documentsData, loading: _documentsLoading, refetch: refetchDocuments } = useQuery(Query.DOC_DOCUMENTS, {
    variables: { folderId: currentFolderId },
    skip: !currentFolderId,
    fetchPolicy: 'cache-and-network',
  })
  // Apollo v3 skip-transition bug: loading can stay true even when data is cached
  const documentsLoading = _documentsLoading && !documentsData

  // Search documents
  const { data: searchData, loading: searchLoading } = useQuery(Query.SEARCH_DOC_DOCUMENTS, {
    variables: { query: searchQuery, projectId },
    skip: !searchQuery || searchQuery.length < 2 || !projectId,
  })

  const allFolders: Folder[] = folderData?.docFolderTree || []
  const isSearching = searchQuery && searchQuery.length >= 2

  // Auto-open document detail when URL has documentId param
  useEffect(() => {
    if (documentIdParam && !documentDetailId) {
      setDocumentDetailId(documentIdParam)
      setDocumentIdParam(null)
    }
  }, [documentIdParam])

  // Clear selection on folder change, search, or view mode change
  useEffect(() => {
    setSelectedDocIds([])
  }, [currentFolderId, searchQuery, viewMode])

  // Build a flat map of all folders for quick lookup
  const folderMap = useMemo(() => {
    const map = new Map<string, Folder>()
    const buildMap = (folders: Folder[]) => {
      for (const folder of folders) {
        map.set(folder.id, folder)
        if (folder.children) {
          buildMap(folder.children)
        }
      }
    }
    buildMap(allFolders)
    return map
  }, [allFolders])

  // Get current folder's children (subfolders)
  const currentSubfolders = useMemo(() => {
    if (!currentFolderId) {
      // At root - show top-level folders
      return allFolders
    }
    const currentFolder = folderMap.get(currentFolderId)
    return currentFolder?.children || []
  }, [currentFolderId, allFolders, folderMap])

  // Get current folder object
  const currentFolder = currentFolderId ? folderMap.get(currentFolderId) : null

  // Get parent folder ID
  const parentFolderId = currentFolder?.parentId || null

  // Build breadcrumb path
  const breadcrumbPath = useMemo(() => {
    const path: { id: string; name: string }[] = []
    let folderId = currentFolderId
    while (folderId) {
      const folder = folderMap.get(folderId)
      if (folder) {
        path.unshift({ id: folder.id, name: folder.name })
        folderId = folder.parentId
      } else {
        break
      }
    }
    return path
  }, [currentFolderId, folderMap])

  // Documents to display
  const documents = isSearching
    ? searchData?.searchDocDocuments || []
    : documentsData?.docDocuments || []

  const handleFolderClick = (folderId: string | null) => {
    setCurrentFolderId(folderId)
    setSearchQuery('')
  }

  const handleCreateFolder = () => {
    setEditingFolderId(null)
    setFolderModalOpen(true)
  }

  const handleFolderModalClose = (saved?: boolean) => {
    setFolderModalOpen(false)
    setEditingFolderId(null)
    if (saved && refetchFolders) {
      refetchFolders()
    }
  }

  const handleUpload = () => {
    if (!currentFolderId) return
    setUploadModalOpen(true)
  }

  const handleUploadComplete = () => {
    setUploadModalOpen(false)
    refetchDocuments()
  }

  // Freelo #32223790: clicking a document opens it in the viewer (PDF with paging, images,
  // spreadsheets) the way the folder icon did; types without a viewer still open the detail dialog,
  // which stays reachable from the row menu for every document.
  const [previewIndex, setPreviewIndex] = useState<number | null>(null)
  const preview = useMemo(() => buildPreviewIndex(documents), [documents])

  const handleDocumentPreview = (documentId: string) => {
    const index = preview.indexById.get(documentId)
    if (index !== undefined) setPreviewIndex(index)
  }

  const handleDocumentDetails = (documentId: string) => {
    setDocumentDetailId(documentId)
  }

  const handleDocumentClick = (documentId: string) => {
    if (preview.indexById.has(documentId)) handleDocumentPreview(documentId)
    else handleDocumentDetails(documentId)
  }

  const handleDocumentDetailClose = (changed?: boolean) => {
    setDocumentDetailId(null)
    if (changed) {
      refetchDocuments()
    }
  }

  // Delete folder mutation
  const [deleteFolder] = useMutation(Mutation.DELETE_DOC_FOLDER, {
    onCompleted: () => {
      toastr.success('', t('DocControl_folder_deleted'))
      refetchFolders?.()
    },
    onError: (error) => {
      toastr.error(t('Common_error'), error.message)
    },
  })

  const handleDeleteFolderConfirm = (folder: { id: string; name: string }) => {
    setDeleteFolderTarget(folder)
  }

  const handleConfirmFolderDelete = async () => {
    if (!deleteFolderTarget) return
    await deleteFolder({ variables: { id: deleteFolderTarget.id } })
    setDeleteFolderTarget(null)
  }

  const deleteFolderStats = useMemo(() => {
    if (!deleteFolderTarget) return null
    const folderNode = findFolderInTree(allFolders, deleteFolderTarget.id)
    if (!folderNode) return null
    return computeFolderContentStats(folderNode)
  }, [deleteFolderTarget, allFolders])

  // Delete document mutation
  const [deleteDocument] = useMutation(Mutation.DELETE_DOC_DOCUMENT, {
    onCompleted: () => {
      toastr.success('', t('DocControl_document_deleted'))
      refetchDocuments()
    },
    onError: (error) => {
      toastr.error(t('Common_error'), error.message)
    },
  })

  const handleDeleteDocumentConfirm = (doc: { id: string; name: string }) => {
    if (!window.confirm(t('DocControl_document_delete_confirm', { name: doc.name }))) return
    deleteDocument({ variables: { id: doc.id } })
  }

  // Bulk delete documents mutation
  const [bulkDeleteDocuments] = useMutation(Mutation.BULK_DELETE_DOC_DOCUMENTS, {
    onCompleted: (data) => {
      const deletedCount = data?.bulkDeleteDocDocuments?.length || 0
      toastr.success('', t('DocControl_documents_bulk_deleted', { count: deletedCount }))
      setSelectedDocIds([])
      refetchDocuments()
    },
    onError: (error) => {
      toastr.error(t('Common_error'), error.message)
    },
  })

  // Extract document IDs from selection (handles both prefixed and unprefixed IDs)
  const selectedDocumentIds = useMemo(() => {
    return selectedDocIds
      .filter((id) => id.startsWith('doc-') || !id.startsWith('folder-'))
      .map((id) => id.replace('doc-', ''))
  }, [selectedDocIds])

  // Extract folder IDs from selection (prefixed with `folder-`)
  const selectedFolderIds = useMemo(() => {
    return selectedDocIds
      .filter((id) => id.startsWith('folder-'))
      .map((id) => id.replace('folder-', ''))
  }, [selectedDocIds])

  // Resolve the current selection (documents + recursively the contents of selected
  // folders) into a flat, de-duplicated document list and open the transmittal modal pre-filled.
  const handleSendAsTransmittal = async () => {
    if (selectedDocIds.length === 0 || isPreparingTransmittal) return
    setIsPreparingTransmittal(true)
    try {
      // Directly selected documents are always part of the currently loaded view
      const directDocs: SelectedDocument[] = documents
        .filter((d: any) => selectedDocumentIds.includes(d.id) && d.currentRevision)
        .map((d: any) => ({ id: d.id, name: d.name, currentRevision: d.currentRevision }))

      // Pull every document inside the selected folders (subfolders included)
      const folderResults = await Promise.all(
        selectedFolderIds.map((folderId) =>
          client.query({
            query: Query.DOC_FOLDER_DOCUMENTS,
            variables: { folderId, includeSubfolders: true },
            fetchPolicy: 'network-only',
          }),
        ),
      )
      const folderDocs: SelectedDocument[] = folderResults
        .flatMap((res) => res.data?.docFolderDocuments || [])
        .filter((d: any) => d.currentRevision)
        .map((d: any) => ({ id: d.id, name: d.name, currentRevision: d.currentRevision }))

      // Merge + de-duplicate (a document can be both directly selected and inside a folder)
      const byId = new Map<string, SelectedDocument>()
      for (const d of [...directDocs, ...folderDocs]) byId.set(d.id, d)
      const merged = Array.from(byId.values())

      if (merged.length === 0) {
        toastr.warning('', t('DocControl_transmittal_no_documents_selected'))
        return
      }

      setTransmittalInitialDocs(merged)
      setTransmittalModalOpen(true)
    } catch (error: any) {
      toastr.error(t('Common_error'), error.message)
    } finally {
      setIsPreparingTransmittal(false)
    }
  }

  // Resolve the selection (documents + recursively the contents of selected
  // folders) to current-revision File ids and stream them as one zip via the
  // auth-gated /download/zip route (it authorizes each file server-side and
  // silently drops any the caller can't access). Cookie auth rides along on the
  // same-origin request.
  const handleBulkDownload = async () => {
    if (selectedDocIds.length === 0 || isPreparingDownload) return
    setIsPreparingDownload(true)
    try {
      const directDocs: any[] = documents.filter(
        (d: any) => selectedDocumentIds.includes(d.id) && d.currentRevision,
      )

      const folderResults = await Promise.all(
        selectedFolderIds.map((folderId) =>
          client.query({
            query: Query.DOC_FOLDER_DOCUMENTS,
            variables: { folderId, includeSubfolders: true },
            fetchPolicy: 'network-only',
          }),
        ),
      )
      const folderDocs: any[] = folderResults
        .flatMap((res) => res.data?.docFolderDocuments || [])
        .filter((d: any) => d.currentRevision)

      // A revision exposes the File id either as a `fileId` scalar (grid view
      // fragment) or nested `file.id` (folder-documents query) — accept both.
      const fileIds = Array.from(
        new Set(
          [...directDocs, ...folderDocs]
            .map((d: any) => d.currentRevision?.fileId || d.currentRevision?.file?.id)
            .filter(Boolean),
        ),
      )

      if (fileIds.length === 0) {
        toastr.warning('', t('DocControl_download_no_files'))
        return
      }

      const qs = fileIds.map((id) => `files=${encodeURIComponent(id)}`).join('&')
      toastr.info('', t('DocControl_download_started'))
      window.open(`/download/zip?${qs}`, '_blank')
    } catch (error: any) {
      toastr.error(t('Common_error'), error.message)
    } finally {
      setIsPreparingDownload(false)
    }
  }

  const handleBulkDeleteClick = () => {
    if (selectedDocIds.length === 0) return
    setDeleteConfirmModalOpen(true)
  }

  const handleConfirmBulkDelete = async () => {
    setIsBulkDeleting(true)
    try {
      // Delete folders first (each recursively removes its subfolders and files)
      for (const folderId of selectedFolderIds) {
        await deleteFolder({ variables: { id: folderId } })
      }
      // Then delete selected documents in a single bulk mutation
      if (selectedDocumentIds.length > 0) {
        await bulkDeleteDocuments({ variables: { ids: selectedDocumentIds } })
      }
      setSelectedDocIds([])
      setDeleteConfirmModalOpen(false)
    } finally {
      setIsBulkDeleting(false)
    }
  }

  // Move mutations. No onError here: @apollo/react-hooks swallows the promise
  // rejection when onError is set, which would let a failed move look successful.
  // We catch each call below and count real successes/failures instead.
  const [moveFolder] = useMutation(Mutation.MOVE_DOC_FOLDER)
  const [moveDocument] = useMutation(Mutation.MOVE_DOC_DOCUMENT)

  const handleMoveFolder = (folder: { id: string; name: string }) => {
    setMoveIsBulk(false)
    setMoveTargets({ folderIds: [folder.id], documentIds: [] })
    setMoveModalOpen(true)
  }

  const handleMoveDocument = (doc: { id: string; name: string }) => {
    setMoveIsBulk(false)
    setMoveTargets({ folderIds: [], documentIds: [doc.id] })
    setMoveModalOpen(true)
  }

  const handleBulkMoveClick = () => {
    if (selectedDocIds.length === 0) return
    setMoveIsBulk(true)
    setMoveTargets({ folderIds: selectedFolderIds, documentIds: selectedDocumentIds })
    setMoveModalOpen(true)
  }

  const handleConfirmMove = async (targetFolderId: string | null) => {
    setIsMoving(true)
    let moved = 0
    let failed = 0
    let lastError: Error | null = null
    try {
      // Move folders first (newParentId may be null for the project root)
      for (const id of moveTargets.folderIds) {
        try {
          await moveFolder({ variables: { id, newParentId: targetFolderId } })
          moved++
        } catch (e) {
          failed++
          lastError = e as Error
        }
      }
      // Documents require a folder; the picker disables Root when documents are present
      for (const id of moveTargets.documentIds) {
        try {
          await moveDocument({ variables: { id, newFolderId: targetFolderId } })
          moved++
        } catch (e) {
          failed++
          lastError = e as Error
        }
      }

      if (moved > 0) {
        toastr.success('', t('DocControl_items_moved', { count: moved }))
      }
      if (failed > 0) {
        toastr.error(t('DocControl_move_failed', { count: failed }), lastError?.message || '')
      }

      refetchFolders?.()
      // DOC_DOCUMENTS is skipped at root (no currentFolderId); refetch() ignores
      // skip and would send folderId: null, which the API rejects.
      if (currentFolderId) refetchDocuments()

      setMoveModalOpen(false)
      // Only a bulk action owns the selection, and only clear it when everything moved
      if (moveIsBulk && failed === 0) {
        setSelectedDocIds([])
      }
    } finally {
      setIsMoving(false)
    }
  }

  const handleClearSelection = () => {
    setSelectedDocIds([])
  }

  const handleDownloadDocument = (doc: { currentRevision?: { fileId?: string } }) => {
    if (doc.currentRevision?.fileId) {
      window.open(`/download/${doc.currentRevision.fileId}`, '_blank')
    }
  }

  const handleShareFolder = (folder: { id: string; name: string }) => {
    setShareTarget({ type: 'folder', id: folder.id, name: folder.name })
    setShareLinkModalOpen(true)
  }

  // Download the whole folder (subfolders included) as a zip. Hits the streaming
  // /download/folder endpoint, which enforces doc-control permissions server-side;
  // cookie auth is sent automatically on this same-origin request.
  const handleDownloadFolder = (folder: { id: string }) => {
    if (downloadingFolderId) return
    setDownloadingFolderId(folder.id)
    toastr.info('', t('DocControl_folder_download_started'))
    window.open(`/download/folder/${folder.id}`, '_blank')
    window.setTimeout(() => setDownloadingFolderId(null), 1500)
  }

  const handleManageAccess = (folder: { id: string }) => {
    setAccessFolderId(folder.id)
  }

  const handleShareDocument = (doc: { id: string; name: string }) => {
    setShareTarget({ type: 'document', id: doc.id, name: doc.name })
    setShareLinkModalOpen(true)
  }

  const handleCopyFolderLink = (folder: { id: string }) => {
    const url = `${window.location.origin}${window.location.pathname}?folderId=${folder.id}`
    navigator.clipboard.writeText(url)
    toastr.success('', t('DocControl_link_copied'))
  }

  const handleCopyDocumentLink = (doc: { id: string; folderId?: string }) => {
    const fId = doc.folderId || currentFolderId
    const params = fId ? `folderId=${fId}&documentId=${doc.id}` : `documentId=${doc.id}`
    const url = `${window.location.origin}${window.location.pathname}?${params}`
    navigator.clipboard.writeText(url)
    toastr.success('', t('DocControl_link_copied'))
  }

  // Browser drag-and-drop handlers
  const handleBrowserDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current++
    if (e.dataTransfer.types.includes('Files')) {
      setIsBrowserDragging(true)
    }
  }, [])

  const handleBrowserDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleBrowserDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    // Clamp: OS folder drags can emit an unbalanced dragleave (a leave with no
    // matching enter), which would drive the counter negative and then never
    // reach 0 again — leaving the overlay stuck. Never go below zero.
    dragCounter.current = Math.max(0, dragCounter.current - 1)
    if (dragCounter.current === 0) {
      setIsBrowserDragging(false)
    }
  }, [])

  // Safety net for the stuck drag overlay: the per-element dragenter/dragleave
  // counter can desync on OS folder drags (dropped or extra events), leaving the
  // blue overlay visible after the upload. Force-clear on any drop, drag-end, or
  // the cursor leaving the window — none of which can fire while a real drag is
  // still hovering the browser, so this can't hide the overlay mid-drag.
  useEffect(() => {
    const clearDragState = () => {
      dragCounter.current = 0
      setIsBrowserDragging(false)
    }
    const onWindowDragLeave = (e: DragEvent) => {
      // relatedTarget === null → the cursor left the document (dragged back out).
      if (e.relatedTarget === null) clearDragState()
    }
    window.addEventListener('drop', clearDragState)
    window.addEventListener('dragend', clearDragState)
    window.addEventListener('dragleave', onWindowDragLeave)
    return () => {
      window.removeEventListener('drop', clearDragState)
      window.removeEventListener('dragend', clearDragState)
      window.removeEventListener('dragleave', onWindowDragLeave)
    }
  }, [])

  const handleBrowserDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsBrowserDragging(false)
    dragCounter.current = 0

    // Drag-and-drop is an alternate path to the same create action as the
    // "Upload" button — gate it the same way (canCreate) so it can't bypass
    // the toolbar button's permission check.
    if (!canCreate) return

    if (!currentFolderId) {
      toastr.warning('', t('DocControl_select_folder_to_upload'))
      return
    }

    // Check for directories
    if (hasDirectoryEntries(e.dataTransfer)) {
      const data = await readDroppedItems(e.dataTransfer)
      setPendingFolderUpload(data)
      setPendingFiles(null)
      setUploadModalOpen(true)
      return
    }

    // Plain files
    const droppedFiles = Array.from(e.dataTransfer.files)
    if (droppedFiles.length > 0) {
      setPendingFolderUpload(null)
      setPendingFiles(droppedFiles)
      setUploadModalOpen(true)
    }
  }, [currentFolderId, canCreate, t])

  const handleUploadModalClose = useCallback(() => {
    setUploadModalOpen(false)
    setPendingFolderUpload(null)
    setPendingFiles(null)
  }, [])

  // Module axis (useModuleActions) AND folder axis (effectivePermission of the
  // folder acted upon). Document rows inherit the current folder's ACL, so all
  // document handlers gate on currentFolderId. Folder rows are gated per row
  // inside FileManagerGrid/DocumentList (each row carries its own level).
  const editFolderHandler = canEdit
    ? (folderId: string) => {
        setEditingFolderId(folderId)
        setFolderModalOpen(true)
      }
    : undefined
  const deleteFolderHandler = canDelete ? handleDeleteFolderConfirm : undefined
  const shareFolderHandler = canAdmin ? handleShareFolder : undefined
  const shareDocumentHandler = canAdmin && folderAtLeast(currentFolderId, 'ADMIN') ? handleShareDocument : undefined
  const deleteDocumentHandler = canDelete && folderAtLeast(currentFolderId, 'MANAGER') ? handleDeleteDocumentConfirm : undefined
  const moveFolderHandler = canEdit ? handleMoveFolder : undefined
  const moveDocumentHandler = canEdit && folderAtLeast(currentFolderId, 'EDITOR') ? handleMoveDocument : undefined
  // Intentionally ungated: FolderShareDialog itself is read-only below MANAGER
  // (server-enforced via effectivePermission), and read affordances stay visible.
  const manageAccessHandler = handleManageAccess

  // Show message if no project is selected
  if (!projectId) {
    return (
      <Alert color="info" className="m-3">
        {t('DocControl_select_project_message')}
      </Alert>
    )
  }

  if (foldersLoading && !folderData) return <DocumentBrowserSkeleton />

  return (
    <>
      <div
        className="relative"
        onDragEnter={handleBrowserDragEnter}
        onDragOver={handleBrowserDragOver}
        onDragLeave={handleBrowserDragLeave}
        onDrop={handleBrowserDrop}
      >
      {/* Drag overlay */}
      {isBrowserDragging && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded border-2 border-dashed border-primary bg-primary/10">
          <div className="text-center">
            <CloudUpload className="h-12 w-12 text-primary mb-2 mx-auto" />
            <p className="text-primary font-bold mb-0">{t('DocControl_drop_here_to_upload')}</p>
          </div>
        </div>
      )}

      <div>
        {/* Toolbar */}
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          {/* Left: actions + breadcrumbs + selection */}
          {canCreate && (!currentFolderId || folderAtLeast(currentFolderId, 'EDITOR')) && (
            <ShadcnButton
              variant="outline"
              size="sm"
              className="h-8"
              onClick={handleCreateFolder}
              title={t('DocControl_create_folder')}
            >
              <FolderPlus className="h-4 w-4" />
              {t('DocControl_new_folder')}
            </ShadcnButton>
          )}

          {canCreate && folderAtLeast(currentFolderId, 'EDITOR') && (
            <ShadcnButton
              size="sm"
              className="h-8"
              onClick={handleUpload}
              disabled={!currentFolderId}
              title={currentFolderId ? t('DocControl_upload_document') : t('DocControl_select_folder_to_upload')}
            >
              <Upload className="h-4 w-4" />
              {t('DocControl_upload')}
            </ShadcnButton>
          )}

          {isSearching ? (
            <span className="text-muted-foreground text-sm">
              <Search className="h-4 w-4 mr-1.5 inline" />
              {t('DocControl_search_results', { query: searchQuery })}
            </span>
          ) : (
            breadcrumbPath.length > 1 && (
              <Breadcrumbs path={breadcrumbPath} onNavigate={handleFolderClick} />
            )
          )}

          {selectedDocIds.length > 0 && (
            <>
              {/* "Send as Transmittal" only ever fires createAndSendDocTransmittal
                  (APPROVE) — there is no lower-tier "save as draft" mutation wired
                  up in the UI, so the entry trigger itself needs APPROVE, not CREATE. */}
              {canApprove && (
                <ShadcnButton
                  variant="default"
                  size="sm"
                  className="h-8"
                  onClick={handleSendAsTransmittal}
                  disabled={isPreparingTransmittal}
                >
                  {isPreparingTransmittal ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                  {t('DocControl_send_as_transmittal')}
                </ShadcnButton>
              )}
              {canEdit && (
                <ShadcnButton
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={handleBulkMoveClick}
                >
                  <FolderInput className="h-4 w-4" />
                  {t('DocControl_move')}
                </ShadcnButton>
              )}
              {/* Download the selection as a zip. Ungated in the UI — the server
                  authorizes each file, so anyone who can see the files can
                  download them. */}
              <ShadcnButton
                variant="outline"
                size="sm"
                className="h-8"
                onClick={handleBulkDownload}
                disabled={isPreparingDownload}
              >
                {isPreparingDownload ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                {t('Common_download')}
              </ShadcnButton>
              {canDelete && (
                <ShadcnButton
                  variant="destructive"
                  size="sm"
                  className="h-8"
                  onClick={handleBulkDeleteClick}
                >
                  <Trash2 className="h-4 w-4" />
                  {t('Common_delete')}
                </ShadcnButton>
              )}
              <Badge variant="secondary" className="font-medium">
                {t('DocControl_selected_count', { count: selectedDocIds.length })}
              </Badge>
              <ShadcnButton
                variant="ghost"
                size="sm"
                className="text-muted-foreground h-7 text-xs"
                onClick={handleClearSelection}
              >
                <X className="h-3.5 w-3.5" />
                {t('Common_clear_selection')}
              </ShadcnButton>
            </>
          )}

          {/* Spacer */}
          <div className="flex-1" />

          {/* Right: view toggle + search */}
          <div className="flex border rounded-md overflow-hidden">
            <ShadcnButton
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              className="rounded-none h-8 px-2.5"
              onClick={() => setViewMode('grid')}
              title={t('DocControl_view_grid')}
            >
              <LayoutGrid className="h-4 w-4" />
            </ShadcnButton>
            <ShadcnButton
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              className="rounded-none h-8 px-2.5"
              onClick={() => setViewMode('list')}
              title={t('DocControl_view_list')}
            >
              <List className="h-4 w-4" />
            </ShadcnButton>
          </div>

          <div className="relative max-w-[220px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <ShadcnInput
              placeholder={t('DocControl_search_placeholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-8"
            />
          </div>
        </div>

        {/* Content */}
        <div>
          {viewMode === 'grid' ? (
            <FileManagerGrid
              folders={isSearching ? [] : currentSubfolders}
              documents={documents}
              loading={(currentFolderId ? documentsLoading : false) || searchLoading}
              currentFolderId={currentFolderId}
              parentFolderId={parentFolderId}
              onFolderClick={handleFolderClick}
              onDocumentClick={handleDocumentClick}
              onDocumentDetails={handleDocumentDetails}
              onEditFolder={editFolderHandler}
              onDeleteFolder={deleteFolderHandler}
              onShareFolder={shareFolderHandler}
              onManageAccess={manageAccessHandler}
              onDownloadFolder={handleDownloadFolder}
              downloadingFolderId={downloadingFolderId}
              onDownloadDocument={handleDownloadDocument}
              onDeleteDocument={deleteDocumentHandler}
              onShareDocument={shareDocumentHandler}
              onCopyFolderLink={handleCopyFolderLink}
              onCopyDocumentLink={handleCopyDocumentLink}
              onMoveFolder={moveFolderHandler}
              onMoveDocument={moveDocumentHandler}
              selection={selectedDocIds}
              onSelectionChange={setSelectedDocIds}
            />
          ) : (
            <DocumentList
              folders={isSearching ? [] : currentSubfolders}
              documents={documents}
              loading={(currentFolderId ? documentsLoading : false) || searchLoading}
              isSearchResults={isSearching}
              currentFolderId={currentFolderId}
              parentFolderId={parentFolderId}
              onFolderClick={handleFolderClick}
              onDocumentClick={handleDocumentClick}
              onDocumentPreview={handleDocumentPreview}
              onDocumentDetails={handleDocumentDetails}
              onRefresh={refetchDocuments}
              onEditFolder={editFolderHandler}
              onDeleteFolder={deleteFolderHandler}
              onShareFolder={shareFolderHandler}
              onManageAccess={manageAccessHandler}
              onDownloadFolder={handleDownloadFolder}
              onDownloadDocument={handleDownloadDocument}
              onDeleteDocument={deleteDocumentHandler}
              onShareDocument={shareDocumentHandler}
              onCopyFolderLink={handleCopyFolderLink}
              onCopyDocumentLink={handleCopyDocumentLink}
              onMoveFolder={moveFolderHandler}
              onMoveDocument={moveDocumentHandler}
              selection={selectedDocIds}
              onSelectionChange={setSelectedDocIds}
            />
          )}
        </div>
      </div>

      {/* Folder Modal */}
      {folderModalOpen && (
        <FolderModal
          isOpen={folderModalOpen}
          onClose={handleFolderModalClose}
          folderId={editingFolderId}
          parentFolderId={currentFolderId}
          projectId={projectId}
        />
      )}

      {/* Upload Modal */}
      {uploadModalOpen && currentFolderId && (
        <DocUploadModal
          isOpen={uploadModalOpen}
          onClose={handleUploadModalClose}
          onComplete={() => {
            handleUploadModalClose()
            refetchDocuments()
            refetchFolders()
          }}
          folderId={currentFolderId}
          initialFolderData={pendingFolderUpload}
          initialFiles={pendingFiles}
        />
      )}

      {/* In-app viewer (Freelo #32223790) */}
      {previewIndex !== null && preview.files.length > 0 && (
        <FilePreviewModal
          isOpen={true}
          files={preview.files}
          currentIndex={Math.min(previewIndex, preview.files.length - 1)}
          onNavigate={setPreviewIndex}
          onClose={() => setPreviewIndex(null)}
        />
      )}

      {/* Document Detail Modal */}
      {documentDetailId && (
        <DocumentDetailModal
          isOpen={!!documentDetailId}
          onClose={handleDocumentDetailClose}
          documentId={documentDetailId}
          projectId={projectId}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteConfirmModalOpen} toggle={() => !isBulkDeleting && setDeleteConfirmModalOpen(false)} centered>
        <ModalHeader toggle={() => !isBulkDeleting && setDeleteConfirmModalOpen(false)}>
          <Trash2 className="h-4 w-4 text-destructive" />
          {t('DocControl_bulk_delete_title')}
        </ModalHeader>
        <ModalBody>
          <p className="mb-2">{t('DocControl_bulk_delete_question')}</p>
          <ul className="mb-0 list-disc pl-5 text-sm">
            {selectedFolderIds.length > 0 && (
              <li>{t('DocControl_bulk_delete_folders_count', { count: selectedFolderIds.length })}</li>
            )}
            {selectedDocumentIds.length > 0 && (
              <li>{t('DocControl_bulk_delete_documents_count', { count: selectedDocumentIds.length })}</li>
            )}
          </ul>
          {selectedFolderIds.length > 0 && (
            <p className="mt-3 mb-0 text-sm text-destructive">
              {t('DocControl_bulk_delete_folders_warning')}
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" disabled={isBulkDeleting} onClick={() => setDeleteConfirmModalOpen(false)}>
            {t('Common_cancel')}
          </Button>
          <Button color="danger" disabled={isBulkDeleting} onClick={handleConfirmBulkDelete}>
            {isBulkDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            {t('Common_delete')}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Folder Confirmation Modal */}
      <DeleteFolderModal
        isOpen={!!deleteFolderTarget}
        folder={deleteFolderTarget}
        stats={deleteFolderStats}
        onConfirm={handleConfirmFolderDelete}
        onCancel={() => setDeleteFolderTarget(null)}
      />

      {/* Move To Folder Modal */}
      <MoveToFolderModal
        isOpen={moveModalOpen}
        folders={allFolders}
        movingFolderIds={moveTargets.folderIds}
        hasDocuments={moveTargets.documentIds.length > 0}
        currentLocationId={isSearching ? undefined : currentFolderId}
        loading={isMoving}
        onConfirm={handleConfirmMove}
        onCancel={() => setMoveModalOpen(false)}
      />

      {/* Share Link Modal */}
      {shareLinkModalOpen && shareTarget && (
        <ShareLinkModal
          isOpen={shareLinkModalOpen}
          onClose={() => {
            setShareLinkModalOpen(false)
            setShareTarget(null)
          }}
          folderId={shareTarget.type === 'folder' ? shareTarget.id : undefined}
          documentId={shareTarget.type === 'document' ? shareTarget.id : undefined}
        />
      )}

      {/* Folder Access (Share) Dialog */}
      <FolderShareDialog
        folderId={accessFolderId}
        projectId={projectId}
        onClose={() => {
          setAccessFolderId(null)
          refetchFolders()
        }}
      />

      {/* Send as Transmittal Modal (pre-filled from the current selection) */}
      {transmittalModalOpen && (
        <QuickTransmittalModal
          isOpen={transmittalModalOpen}
          initialDocuments={transmittalInitialDocs}
          onClose={(updated) => {
            setTransmittalModalOpen(false)
            setTransmittalInitialDocs([])
            if (updated) setSelectedDocIds([])
          }}
        />
      )}
      </div>
    </>
  )
}

export default DocumentBrowser
